import React, { useCallback, useEffect, useState } from "react";
import HelmetWrapper from "../../HelmetWrapper";
import { Col, Container, Row } from "react-bootstrap";
import {
  GPS_STATUS_OPTIONS,
  STATUS_OPTIONS,
  trackingTabs,
} from "./TrackingUtils";
import BreakRules from "./BreakRules";
import { useGetFetchNew } from "../../data/Queries";
import {
  trackingSystemAttendanceLogs,
  trackingSystemBreakRules,
  trackingSystemLocationLogs,
} from "../../data/ApiEndpoints";
import BreakDetailsModal from "./BreakDetailsModal";
import FilterContainer from "../common-components/filters/filter-container";
import GenericSelectFilter from "../common-components/filters/generic-select-filter";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { fetchInHouseUsersData } from "../../redux/reducers/dropdownApiSlice";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import GymFilter from "../common-components/filters/gym-filter";
import AttendenceLogs from "./AttendenceLogs";
import DateFilter from "../common-components/filters/date-filter";
import "../../styles/tracking-system/employee-date-range.css";
import CommonStructuredV2 from "../common-components/CommonStructuredV2";
import Modal from "react-modal";
import BreakRulesForm from "./BreakRulesForm";
import OutOfRangeWebhook from "./OutOfRangeWebhook";
import TrackingStats from "./TrackingStats";
import LocationLogs from "./LocationLogs";
import LocationLogsWebhook from "./LocationLogsWebhook";

const TrackingSystemLanding = ({ userInfoData }) => {
  const [refetchFunctions, setRefetchFunctions] = useState({});
  const [activeTab, setActiveTab] = useState("attendance-logs");
  const [statsRefetchTrigger, setStatsRefetchTrigger] = useState(0);

  // Break rules modal state
  const [breakRulesModal, setBreakRulesModal] = useState({
    isOpen: false,
    mode: "create", // "create" or "edit"
    rowData: null,
  });

  const handleReload = () => {
    if (refetchFunctions.refetchBreakRules) {
      refetchFunctions.refetchBreakRules();
    }
    if (refetchFunctions.refetchAttendanceLogs) {
      refetchFunctions.refetchAttendanceLogs();
    }
    if (refetchFunctions.refetchLocationLogs) {
      refetchFunctions.refetchLocationLogs();
    }
    // Trigger stats refetch
    setStatsRefetchTrigger((prev) => prev + 1);
  };

  // Handle create break button click
  const handleCreateBreak = () => {
    setBreakRulesModal({
      isOpen: true,
      mode: "create",
      rowData: null,
    });
  };

  // Handle edit break button click
  const handleEditBreak = (rowData) => {
    setBreakRulesModal({
      isOpen: true,
      mode: "edit",
      rowData: rowData,
    });
  };

  // Close break rules modal
  const handleCloseBreakRulesModal = () => {
    setBreakRulesModal({
      isOpen: false,
      mode: "create",
      rowData: null,
    });
  };

  return (
    <HelmetWrapper title="WTF: Tracking System">
      <Container className="bg-cloud-k px-50px" fluid>
        <CommonStructuredV2
          pageHeading={`Tracking System`}
          createBtnTxt={activeTab === "break-rules" ? "Create Break" : null}
          onClickCreateBtn={handleCreateBreak}
          isReload={handleReload}
        />

        <Row>
          <Col>
            <TrackingSystemTabs
              userInfoData={userInfoData}
              setRefetchFunctions={setRefetchFunctions}
              onEditBreak={handleEditBreak}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              statsRefetchTrigger={statsRefetchTrigger}
            />
          </Col>
        </Row>

        {/* Break Rules Modal */}
        <Modal
          isOpen={breakRulesModal.isOpen}
          onRequestClose={handleCloseBreakRulesModal}
          contentLabel="Break Rules Modal"
          ariaHideApp={false}
        >
          <BreakRulesForm
            refetchBreakRules={refetchFunctions.refetchBreakRules}
            onCloseModal={handleCloseBreakRulesModal}
            modValues={{
              isModOpen: breakRulesModal.isOpen,
              cardName: breakRulesModal.mode,
              rowData: breakRulesModal.rowData,
            }}
            setModValues={() => {}} // Not needed since we handle state in parent
            token={userInfoData?.token}
          />
        </Modal>

        {/* Out of Range Webhook Notifications */}
        <OutOfRangeWebhook
          userInfoData={userInfoData}
          onOutOfRangeDetected={() => {
            // Trigger stats refresh when out of range is detected
            setStatsRefetchTrigger((prev) => prev + 1);
          }}
        />
      </Container>
    </HelmetWrapper>
  );
};

export default TrackingSystemLanding;

const TrackingSystemTabs = ({
  userInfoData,
  setRefetchFunctions,
  onEditBreak,
  activeTab,
  onTabChange,
  statsRefetchTrigger,
}) => {
  const dispatch = useDispatch();
  const { inhouseUsers } = useSelector((state) => state.dropdown);
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);
  const [breakTypeOptions, setBreakTypeOptions] = useState([]);
  const [staffTypeFilter, setStaffTypeFilter] = useState({
    label: "All Staff",
    value: "",
  });

  // Filter states similar to ticket dashboard
  const [searchTerm, setSearchTerm] = useState("");
  // const [startDate, setStartDate] = useState(
  //   moment().startOf("week").format("YYYY-MM-DD")
  // );
  // const [endDate, setEndDate] = useState(
  //   moment().endOf("week").format("YYYY-MM-DD")
  // );
  const [userFilter, setUserFilter] = useState(null);
  const [breakTypeFilter, setBreakTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const [singleDateFilter, setSingleDateFilter] = useState(
    moment().format("YYYY-MM-DD")
  );

  // Employee-specific date range filters
  const [employeeDateRange, setEmployeeDateRange] = useState(null);
  const [selectedDateRangeOption, setSelectedDateRangeOption] =
    useState("today");

  const [gpsStatusFilter, setGpsStatusFilter] = useState(null);
  const [gymFilter, setGymFilter] = useState(null);

  // Time range filter for out of range logs (from stats navigation)
  const [timeRangeFilter, setTimeRangeFilter] = useState(null);

  // Modal state
  const [showBreakDetailsModal, setShowBreakDetailsModal] = useState(false);
  const [selectedBreakLog, setSelectedBreakLog] = useState(null);

  const {
    data: breakRulesData,
    isLoading: breakRulesLoading,
    refetch: refetchBreakRules,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "break_rules",
    url: trackingSystemBreakRules,
    filters: {
      limit: pageLimit || 10,
      page: isPage + 1,
    },
  });

  const commonFilters = {
    limit: pageLimit || 10,
    page: isPage + 1,
    // Always use start_date and end_date format
    ...(userFilter && employeeDateRange
      ? { start_date: employeeDateRange.start, end_date: employeeDateRange.end }
      : singleDateFilter && {
          start_date: singleDateFilter,
          end_date: singleDateFilter,
        }),
    ...(searchTerm.trim() && { search: searchTerm.trim() }),
    ...(userFilter && { user_id: userFilter.value }),
    ...(gymFilter && { gym_id: gymFilter.value }),
    ...(staffTypeFilter &&
      staffTypeFilter.value && {
        account_type: staffTypeFilter.value,
      }),
  };

  const {
    data: attendanceLogsData,
    refetch: refetchAttendanceLogs,
    isLoading: attendanceLogsLoading,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "attendance_logs",
    url: trackingSystemAttendanceLogs,
    filters: {
      ...commonFilters,
    },
  });

  // Build location logs URL directly to avoid encoding issues with time format
  const buildLocationLogsUrl = () => {
    const dateFilter = userFilter && employeeDateRange
      ? { start_date: employeeDateRange.start, end_date: employeeDateRange.end }
      : singleDateFilter
        ? { start_date: singleDateFilter, end_date: singleDateFilter }
        : {};

    let url = `${trackingSystemLocationLogs}?limit=${pageLimit || 20}&page=${isPage + 1}&status=out_of_range`;

    if (dateFilter.start_date) url += `&start_date=${dateFilter.start_date}`;
    if (dateFilter.end_date) url += `&end_date=${dateFilter.end_date}`;
    if (searchTerm.trim()) url += `&search=${searchTerm.trim()}`;
    if (userFilter?.value) url += `&user_id=${userFilter.value}`;
    if (gymFilter?.value) url += `&gym_id=${gymFilter.value}`;
    if (staffTypeFilter?.value) url += `&account_type=${staffTypeFilter.value}`;
    if (timeRangeFilter?.from_time) url += `&start_time=${timeRangeFilter.from_time}`;
    if (timeRangeFilter?.to_time) url += `&end_time=${timeRangeFilter.to_time}`;

    return url;
  };

  const {
    data: locationLogsData,
    refetch: refetchLocationLogs,
    isLoading: locationLogsLoading,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "location_logs",
    url: buildLocationLogsUrl(),
  });

  // console.log("attendanceLogsData", attendanceLogsData);

  // Fetch users for filter
  useEffect(() => {
    if (userInfoData?.token) {
      dispatch(
        fetchInHouseUsersData({
          token: userInfoData?.token,
          qps:
            staffTypeFilter && staffTypeFilter.value
              ? `account_type=${staffTypeFilter.value}`
              : "account_type=receptionist,trainer",
          pageNo: 1,
          limit: 100,
        })
      );
    }
  }, [dispatch, userInfoData?.token, staffTypeFilter]);

  useEffect(() => {
    if (activeTab === "attendance-logs") {
      refetchAttendanceLogs();
    } else if (activeTab === "break-rules") {
      refetchBreakRules();
    } else if (activeTab === "location-logs") {
      refetchLocationLogs();
    }
  }, [
    searchTerm,
    isPage,
    pageLimit,
    userFilter,
    activeTab,
    statusFilter,
    gymFilter,
    singleDateFilter,
    employeeDateRange,
    staffTypeFilter,
    timeRangeFilter,
    refetchAttendanceLogs,
    refetchBreakRules,
    refetchLocationLogs,
  ]);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    if (!userInfoData?.token) return;

    try {
      // Fetch break types
      const breakTypesResult = await dispatch(
        fetchApiData({
          method: "GET",
          endpoint: trackingSystemBreakRules,
          token: userInfoData.token,
        })
      );

      if (
        breakTypesResult?.payload?.status &&
        breakTypesResult?.payload?.data
      ) {
        const breakTypes = breakTypesResult.payload.data.map((breakType) => ({
          label: breakType.name,
          value: breakType.uid,
        }));
        setBreakTypeOptions(breakTypes);
      }

      // Fetch users for filter
      dispatch(
        fetchInHouseUsersData({
          token: userInfoData?.token,
          qps:
            staffTypeFilter && staffTypeFilter.value
              ? `account_type=${staffTypeFilter.value}`
              : "account_type=receptionist,trainer",
          pageNo: 1,
          limit: 100,
        })
      );
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, [dispatch, userInfoData?.token, staffTypeFilter]);

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Pass refetch functions to parent component
  useEffect(() => {
    if (setRefetchFunctions) {
      setRefetchFunctions({
        refetchBreakRules,
        refetchAttendanceLogs,
        refetchLocationLogs,
      });
    }
  }, [
    refetchBreakRules,
    refetchAttendanceLogs,
    refetchLocationLogs,
    setRefetchFunctions,
  ]);

  // Handle location logs update from WebSocket
  const handleLocationUpdate = useCallback(
    (newData) => {
      console.log("📍 Location update received:", newData);
      // Refetch location logs to get the latest data
      if (activeTab === "location-logs") {
        refetchLocationLogs();
      }
    },
    [activeTab, refetchLocationLogs]
  );

  // Handle view break details
  const handleViewBreakDetails = (breakLog) => {
    setSelectedBreakLog(breakLog);
    setShowBreakDetailsModal(true);
  };

  const handleCloseBreakDetailsModal = () => {
    setShowBreakDetailsModal(false);
    setSelectedBreakLog(null);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSingleDateFilter(moment().format("YYYY-MM-DD"));
    setUserFilter(null);
    setBreakTypeFilter(null);
    setStatusFilter(null);
    setEmployeeDateRange(null);
    setSelectedDateRangeOption("today");
    setIsPage(0);
  };

  // Generate date range options
  const getDateRangeOptions = () => {
    const today = moment();
    const options = [
      {
        label: "Today",
        value: "today",
        start: today.format("YYYY-MM-DD"),
        end: today.format("YYYY-MM-DD"),
      },
      {
        label: "Yesterday",
        value: "yesterday",
        start: today.clone().subtract(1, "day").format("YYYY-MM-DD"),
        end: today.clone().subtract(1, "day").format("YYYY-MM-DD"),
      },
      {
        label: "Last 7 Days",
        value: "last7days",
        start: today.clone().subtract(6, "days").format("YYYY-MM-DD"),
        end: today.format("YYYY-MM-DD"),
      },
      {
        label: "Last 30 Days",
        value: "last30days",
        start: today.clone().subtract(29, "days").format("YYYY-MM-DD"),
        end: today.format("YYYY-MM-DD"),
      },
    ];

    // Add last 6 months
    for (let i = 0; i < 6; i++) {
      const monthDate = today.clone().subtract(i, "months");
      options.push({
        label: monthDate.format("MMM").toUpperCase(),
        value: `month_${i}`,
        start: monthDate.clone().startOf("month").format("YYYY-MM-DD"),
        end: monthDate.clone().endOf("month").format("YYYY-MM-DD"),
      });
    }

    return options;
  };

  // Handle employee filter change
  const handleUserFilterChange = (selectedUser) => {
    setUserFilter(selectedUser);
    setIsPage(0);

    if (selectedUser) {
      // Set default date range to "Today" when employee is selected
      const todayOption = getDateRangeOptions().find(
        (opt) => opt.value === "today"
      );
      setEmployeeDateRange({
        start: todayOption.start,
        end: todayOption.end,
      });
      setSelectedDateRangeOption("today");
    } else {
      // Clear employee date range when no employee is selected
      setEmployeeDateRange(null);
      setSelectedDateRangeOption("today");
    }
  };

  // Handle date range option change
  const handleDateRangeOptionChange = (option) => {
    setSelectedDateRangeOption(option.value);
    if (userFilter) {
      setEmployeeDateRange({
        start: option.start,
        end: option.end,
      });
      setIsPage(0);
    }
  };

  // Render filters component
  const renderFilters = () => {
    return (
      <Row className="mb-4">
        <Col>
          <div className="">
            <div className="mt-3">
              <FilterContainer onReset={handleResetFilters}>
                {/* Search Filter */}
                {/* <div className="filter-search-wrapper">
                  <input
                    type="text"
                    placeholder="Search by employee name or break type..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="premium-search-input"
                  />
                </div> */}
                {/* User Filter */}
                <GenericSelectFilter
                  options={[
                    { label: "All Staff", value: "" },
                    { label: "Receptionist", value: "receptionist" },
                    { label: "Trainer", value: "trainer" },
                  ]}
                  selectedOption={staffTypeFilter}
                  setSelectedOption={(opt) => {
                    setStaffTypeFilter(opt);
                    // Reset dependent filters when staff type changes
                    setUserFilter(null);
                    setIsPage(0);
                  }}
                  name="Staff Type"
                  placeholder="Select Staff Type"
                  isSearchable={false}
                />
                {inhouseUsers && inhouseUsers.length > 0 && (
                  <GenericSelectFilter
                    options={[
                      { label: "All Employees", value: "" },
                      ...inhouseUsers,
                    ]}
                    selectedOption={userFilter}
                    setSelectedOption={handleUserFilterChange}
                    name="Employee"
                    placeholder="Select Employee"
                    isSearchable={true}
                  />
                )}

                {/* Show single date filter only when no employee is selected */}
                {!userFilter && (
                  <DateFilter
                    selectedDate={singleDateFilter}
                    setSelectedDate={setSingleDateFilter}
                  />
                )}

                {/* Show employee date range selector when employee is selected */}
                {userFilter && (
                  <div className="employee-date-range-container">
                    <div className="employee-date-range-tabs">
                      {getDateRangeOptions().map((option) => (
                        <button
                          key={option.value}
                          className={`date-range-tab ${
                            selectedDateRangeOption === option.value
                              ? "active"
                              : ""
                          }`}
                          data-value={option.value}
                          onClick={() => handleDateRangeOptionChange(option)}
                          title={`${option.start} to ${option.end}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Break Type Filter */}
                {breakTypeOptions && breakTypeOptions.length > 0 && (
                  <GenericSelectFilter
                    options={[
                      { label: "All Break Types", value: "" },
                      ...breakTypeOptions,
                    ]}
                    selectedOption={breakTypeFilter}
                    setSelectedOption={setBreakTypeFilter}
                    name="Break Type"
                    placeholder="Select Break Type"
                    isSearchable={true}
                  />
                )}
                {/* Status Filter */}
                {activeTab === "location-tracking" && (
                  <GenericSelectFilter
                    options={STATUS_OPTIONS}
                    selectedOption={
                      typeof statusFilter === "string" && statusFilter
                        ? STATUS_OPTIONS.find(
                            (opt) => opt.value === statusFilter
                          )
                        : statusFilter
                    }
                    setSelectedOption={setStatusFilter}
                    name="Status"
                    placeholder="Select Location Status"
                    isSearchable={false}
                  />
                )}

                {/* Date Range Filter */}
                {/* <DateRangerFilter
                  startDate={startDate}
                  endDate={endDate}
                  onDateChange={handleDateRangeChange}
                /> */}

                {/* GPS Status Filter */}
                {activeTab === "location-tracking" && (
                  <GenericSelectFilter
                    options={GPS_STATUS_OPTIONS}
                    selectedOption={
                      typeof gpsStatusFilter === "string" && gpsStatusFilter
                        ? GPS_STATUS_OPTIONS.find(
                            (opt) => opt.value === gpsStatusFilter
                          )
                        : gpsStatusFilter
                    }
                    setSelectedOption={setGpsStatusFilter}
                    name="GPS Status"
                    placeholder="Select GPS Status"
                    isSearchable={false}
                  />
                )}

                <GymFilter
                  selectedGym={gymFilter}
                  setSelectedGym={setGymFilter}
                />
              </FilterContainer>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <div className="lms-v2-tabs-container payrole-tabs-container mt15">
        {trackingTabs.map((tab, idx) => (
          <div
            key={idx}
            className={`lms-v2-tab${
              activeTab === tab.value ? " lms-v2-tab-active" : ""
            }`}
            onClick={() => onTabChange(tab.value)}
            style={{ cursor: "pointer" }}
          >
            <span>{tab.label}</span>
          </div>
        ))}
      </div>

      {/* Filters Section */}
      {activeTab !== "break-rules" && renderFilters()}

      {activeTab === "break-rules" && (
        <BreakRules
          userInfoData={userInfoData}
          breakRulesData={breakRulesData}
          breakRulesLoading={breakRulesLoading}
          refetchBreakRules={refetchBreakRules}
          isPage={isPage}
          setIsPage={setIsPage}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          onEditBreak={onEditBreak}
        />
      )}

      {activeTab === "attendance-logs" && (
        <>
          <TrackingStats
            userInfoData={userInfoData}
            refetchTrigger={statsRefetchTrigger}
            onNavigateToOutOfRange={(staffType) => {
              onTabChange("location-logs");
              // Set staff type filter when navigating
              setStaffTypeFilter({
                label: staffType === "trainer" ? "Trainer" : "Receptionist",
                value: staffType,
              });
              // Set time range filter to current hour
              setTimeRangeFilter({
                from_time: moment().startOf("hour").format("HH:mm"),
                to_time: moment().endOf("hour").format("HH:mm"),
              });
            }}
          />

          <AttendenceLogs
            attendanceLogsData={attendanceLogsData}
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            handleViewBreakDetails={handleViewBreakDetails}
            userInfoData={userInfoData}
            attendanceLogsLoading={attendanceLogsLoading}
          />
        </>
      )}

      {activeTab === "location-logs" && (
        <>
          {/* Active Filters Banner */}
          {staffTypeFilter && staffTypeFilter.value && (
            <Row className="mb-3">
              <Col>
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "2px solid #dc2626",
                    borderRadius: "12px",
                    padding: "16px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "0 2px 8px rgba(220, 38, 38, 0.1)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#dc2626",
                        color: "white",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      📊 Filtered View
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        Showing Out of Range Logs for:{" "}
                        <span
                          style={{
                            color: "#dc2626",
                            textTransform: "uppercase",
                          }}
                        >
                          {staffTypeFilter.label}S
                        </span>
                      </div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>
                        📅 Date: {singleDateFilter} • ⏰ Time Range: Last 1 hour
                        ({moment().startOf("hour").format("HH:mm")} -{" "}
                        {moment().format("HH:mm")})
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setStaffTypeFilter({ label: "All Staff", value: "" });
                      setTimeRangeFilter(null);
                    }}
                    style={{
                      backgroundColor: "#fee2e2",
                      border: "1px solid #dc2626",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#dc2626",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#dc2626";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#fee2e2";
                      e.currentTarget.style.color = "#dc2626";
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </Col>
            </Row>
          )}

          <LocationLogs
            locationLogsData={locationLogsData}
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            locationLogsLoading={locationLogsLoading}
          />

          {/* Location Logs WebSocket */}
          <LocationLogsWebhook
            userInfoData={userInfoData}
            onLocationUpdate={handleLocationUpdate}
          />
        </>
      )}

      {/* Break Details Modal */}
      <BreakDetailsModal
        show={showBreakDetailsModal}
        onClose={handleCloseBreakDetailsModal}
        breakLog={selectedBreakLog}
        userInfoData={userInfoData}
      />
    </>
  );
};
